import styled from 'styled-components';

import CheckmarkIconSvg from '../../../assets/icons/icon-checkmark.svg?react';
import CloseIconSvg from '../../../assets/icons/icon-close.svg?react';
import EditIconSvg from '../../../assets/icons/icon-my-page-edit.svg?react';
import { BaseButton, BaseInput } from '../../../shared/ui';

type MyPageProfileEditFieldTypographyVariant = 'compact' | 'default';

export const Field = styled.div.attrs({
  'data-ui': 'my-page-profile-edit-field',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(7)};
`;

export const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const FieldLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
`;

export const FieldSurface = styled.div<{ $isEditing: boolean }>`
  min-height: ${({ theme }) => theme.scale.px(56)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(8)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(10)}
    ${({ theme }) => theme.scale.px(14)};
  transition: box-shadow 160ms ease;

  &:focus-within {
    box-shadow: ${({ $isEditing, theme }) =>
      $isEditing
        ? `0 0 0 ${theme.scale.px(2)} ${theme.colors.brand.primary}`
        : 'none'};
  }
`;

export const FieldValue = styled.p<{
  $typographyVariant: MyPageProfileEditFieldTypographyVariant;
}>`
  flex: 1;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
  font-size: ${({ $typographyVariant, theme }) =>
    $typographyVariant === 'compact' ? theme.scale.px(12) : theme.scale.px(16)};
  line-height: ${({ $typographyVariant, theme }) =>
    $typographyVariant === 'compact' ? theme.scale.px(15) : theme.scale.px(30)};
  color: ${({ theme }) => theme.colors.text.subdued};
`;

export const EditButtonSlot = styled.div`
  width: ${({ theme }) => theme.scale.px(68)};
  flex: 0 0 ${({ theme }) => theme.scale.px(68)};
  display: flex;
  justify-content: flex-end;
`;

export const EditButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(24)};
  display: grid;
  place-items: center;
`;

export const EditIcon = styled(EditIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(18)};
  height: ${({ theme }) => theme.scale.px(18)};

  & path {
    fill: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const EditModeRoot = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

export const FieldInput = styled(BaseInput)<{
  $typographyVariant: MyPageProfileEditFieldTypographyVariant;
}>`
  flex: 1;
  min-width: 0;
  display: block;
  width: 100%;
  margin: 0;
  font-size: ${({ $typographyVariant, theme }) =>
    $typographyVariant === 'compact' ? theme.scale.px(12) : theme.scale.px(16)};
  line-height: ${({ $typographyVariant, theme }) =>
    $typographyVariant === 'compact' ? theme.scale.px(15) : theme.scale.px(30)};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
  }
`;

export const ActionRow = styled.div`
  width: ${({ theme }) => theme.scale.px(68)};
  flex: 0 0 ${({ theme }) => theme.scale.px(68)};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

export const ActionButton = styled(BaseButton)<{
  $tone: 'accept' | 'cancel';
}>`
  width: ${({ theme }) => theme.scale.px(30)};
  height: ${({ theme }) => theme.scale.px(30)};
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.surface.base};
  background: ${({ $tone, theme }) =>
    $tone === 'accept'
      ? theme.colors.support.success
      : theme.colors.support.danger};
`;

export const AcceptIcon = styled(CheckmarkIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(13)};
  height: ${({ theme }) => theme.scale.px(11)};
`;

export const CancelIcon = styled(CloseIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};

  & path {
    stroke: currentColor;
  }
`;
